import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, formatKES } from "../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Smartphone } from "lucide-react";

/**
 * M-Pesa STK Push payment dialog. MOCKED on backend.
 */
export default function PaymentDialog({ open, onOpenChange, amount, purpose, tierId, referenceId, defaultPhone, onSuccess, title, description }) {
  const [phone, setPhone] = useState(defaultPhone || "254");
  const [status, setStatus] = useState("idle"); // idle | processing | success | failed
  const [receipt, setReceipt] = useState("");

  const pay = async () => {
    if (!/^254[17]\d{8}$/.test(phone)) {
      toast.error("Phone must be in format 254XXXXXXXXX");
      return;
    }
    setStatus("processing");
    try {
      const { data } = await api.post("/payments/stk-push", {
        phone,
        amount,
        purpose,
        tier_id: tierId,
        reference_id: referenceId,
      });
      if (data.success) {
        setStatus("success");
        setReceipt(data.receipt);
        toast.success("Payment confirmed");
        setTimeout(() => {
          onSuccess?.(data);
          onOpenChange(false);
          setStatus("idle");
        }, 1500);
      } else {
        setStatus("failed");
        toast.error(data.message || "Payment failed");
      }
    } catch (e) {
      setStatus("failed");
      toast.error(e?.response?.data?.detail || "Payment error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F8F9FE] border-[#E4E7F5]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title || "Pay with M-Pesa"}</DialogTitle>
          <DialogDescription>{description || `You will receive an STK push to enter your M-Pesa PIN.`}</DialogDescription>
        </DialogHeader>

        {status === "idle" && (
          <div className="space-y-4">
            <div className="bg-[#EEF0FA] rounded-md p-5 flex items-center justify-between">
              <div className="label-eyebrow">Amount</div>
              <div className="font-display text-2xl font-bold text-[#1E3A8A]">{formatKES(amount)}</div>
            </div>
            <div>
              <Label htmlFor="mpesa-phone" className="label-eyebrow">M-Pesa Phone Number</Label>
              <Input
                id="mpesa-phone"
                data-testid="mpesa-phone-input"
                placeholder="254712345678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-2 border-[#E4E7F5] focus:ring-[#1E3A8A]"
              />
              <p className="text-xs text-[#5B6478] mt-2">Format: 254XXXXXXXXX (e.g. 254712345678)</p>
            </div>
            <div className="text-xs text-[#5B6478] bg-amber-50 border border-amber-200 rounded-md p-3">
              <strong>Test mode:</strong> M-Pesa is currently mocked. Any valid phone number will trigger a simulated STK push.
            </div>
          </div>
        )}

        {status === "processing" && (
          <div className="py-10 flex flex-col items-center gap-4">
            <Smartphone className="w-12 h-12 text-[#1E3A8A]" />
            <Loader2 className="w-6 h-6 animate-spin text-[#7C3AED]" />
            <div className="text-center">
              <div className="font-display font-semibold">Waiting for confirmation</div>
              <div className="text-sm text-[#5B6478] mt-1">Enter your M-Pesa PIN on your phone</div>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="py-8 flex flex-col items-center gap-3">
            <CheckCircle2 className="w-14 h-14 text-[#1E3A8A]" />
            <div className="font-display font-bold text-xl">Payment Successful</div>
            <div className="text-sm text-[#5B6478]">Receipt: {receipt}</div>
          </div>
        )}

        {status === "failed" && (
          <div className="py-8 flex flex-col items-center gap-3">
            <XCircle className="w-14 h-14 text-[#B91C1C]" />
            <div className="font-display font-bold text-xl">Payment Failed</div>
            <div className="text-sm text-[#5B6478]">Please try again</div>
          </div>
        )}

        {(status === "idle" || status === "failed") && (
          <DialogFooter>
            <Button data-testid="mpesa-cancel-btn" variant="outline" onClick={() => onOpenChange(false)} className="border-[#E4E7F5]">
              Cancel
            </Button>
            <Button
              data-testid="mpesa-pay-btn"
              onClick={pay}
              className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
            >
              Pay {formatKES(amount)}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
